import React, { Suspense, useMemo, useState } from "react";
import {
  AppShell,
  type AccountState,
  type NavigationTarget,
  type ShellApp,
  type UserMenuItem,
} from "@bellomyinsightstech/app-shell";
import { useAuth } from "./state/AuthContext";
import { LoadingState } from "./components/LoadingState";
import { AuthCallback } from "./components/AuthCallback";
import { SsoCallback } from "./components/SsoCallback";
import { NotificationBanner } from "./components/NotificationBanner";
import { ExportProgressOverlay } from "./components/ExportProgressOverlay";
import { SessionManager } from "./components/SessionManager";
import { UserClaims } from "./types";
import { appConfig, AudienceResolutionError, getAudience } from "./config";
import { useFeatureAvailability } from "./state/useFeatureAvailability";
import { FeatureGate } from "./components/FeatureGate";
import { FeatureAvailabilityPage } from "./components/FeatureAvailabilityPage";
import { canNavigateTo } from "./navigationGuard";
import { useGlobalDialogEscape } from "./components/useGlobalDialogEscape";
import { getCurrentShellItemId, getShellNavigation, shellSettingsIcon } from "./shellNavigation";
import {
  COCREATION_TOOLS_HANDOFF_PATH,
} from "./services/cocreationSso";
import { CocreationLaunchPage } from "./components/CocreationLaunchPage";

const getShellApps = (): readonly ShellApp[] => [
  { id: "panel-insights", name: "Panel Insights", href: window.location.origin },
  {
    id: "cocreation",
    name: "Cocreation Toolkit",
    href: appConfig.stage === "prod"
      ? new URL(COCREATION_TOOLS_HANDOFF_PATH, window.location.origin).toString()
      : "",
  },
  { id: "other-bellomy-apps", name: "Other Bellomy apps", href: "" },
];

const isBellomyUser = (user: UserClaims | null | undefined): boolean => {
  const permissions = user?.permissions || [];
  const groups = user?.groups || [];
  return permissions.includes("UberUser") || groups.includes("PG_BELLOMY_USERS");
};

const StudyDetailPage = React.lazy(() =>
  import("./components/StudyDetailPage").then((m) => ({ default: m.StudyDetailPage }))
);
const PanelistsPage = React.lazy(() =>
  import("./components/PanelistsPage").then((m) => ({ default: m.PanelistsPage }))
);
const StudiesInsightsPage = React.lazy(() =>
  import("./components/StudiesInsightsPage").then((m) => ({ default: m.StudiesInsightsPage }))
);
const HomePage = React.lazy(() =>
  import("./components/HomePage").then((m) => ({ default: m.HomePage }))
);
const PanelChatPage = React.lazy(() =>
  import("./components/PanelChatPage").then((m) => ({ default: m.PanelChatPage }))
);
const AutomationPage = React.lazy(() =>
  import("./components/AutomationPage").then((m) => ({ default: m.AutomationPage }))
);
const ToolsPage = React.lazy(() =>
  import("./components/ToolsPage").then((m) => ({ default: m.ToolsPage }))
);
const LazyPanelistDialogDesignerPage = React.lazy(() =>
  import("./components/PanelistDialogDesignerPage").then((m) => ({ default: m.PanelistDialogDesignerPage }))
);
const LazyPanelistScreenerDesignPage = React.lazy(() => import("./components/PanelistScreenerDesignPage").then(m => ({ default: m.PanelistScreenerDesignPage })));
const LazyPanelFieldManagerPage = React.lazy(() =>
  import("./components/PanelFieldManagerPage").then((m) => ({ default: m.PanelFieldManagerPage }))
);
const LazyPanelistCategoriesPage = React.lazy(() =>
  import("./components/PanelistCategoriesPage").then((m) => ({ default: m.PanelistCategoriesPage }))
);
const LazyPanelFieldGroupsPage = React.lazy(() =>
  import("./components/PanelFieldGroupsPage").then((m) => ({ default: m.PanelFieldGroupsPage }))
);
const LazyDataBrowserPage = React.lazy(() =>
  import("./components/DataBrowserPage").then((m) => ({ default: m.DataBrowserPage }))
);
const LazyDataSearchPage = React.lazy(() =>
  import("./components/DataSearchPage").then((m) => ({ default: m.DataSearchPage }))
);
const LazyAudienceCopyToolPage = React.lazy(() =>
  import("./components/AudienceCopyToolPage").then((m) => ({ default: m.AudienceCopyToolPage }))
);
const LazyStudyFieldManagerPage = React.lazy(() =>
  import("./components/StudyFieldManagerPage").then((m) => ({ default: m.StudyFieldManagerPage }))
);
const LazyStudyDialogDesignerPage = React.lazy(() =>
  import("./components/StudyDialogDesignerPage").then((m) => ({ default: m.StudyDialogDesignerPage }))
);
const LazyDesignSystemPage = React.lazy(() =>
  import("./components/DesignSystemPage").then((m) => ({ default: m.DesignSystemPage }))
);
const CrossStudyReportPage = React.lazy(() =>
  import("./components/CrossStudyReportPage").then((m) => ({ default: m.CrossStudyReportPage }))
);
const loadSchedulingPage = () =>
  import("./components/SchedulingPage").then((m) => ({ default: m.SchedulingPage }));
const SchedulingPage = React.lazy(loadSchedulingPage);
const CommunicationsPage = React.lazy(() =>
  import("./components/CommunicationsPage").then((m) => ({ default: m.CommunicationsPage }))
);
const DataExplorerPage = React.lazy(() =>
  import("./components/analytics/AnalyticsPages").then((m) => ({ default: m.DataExplorerPage }))
);
const CrosstabsPage = React.lazy(() =>
  import("./components/analytics/crosstabs/CrosstabsPage").then((m) => ({ default: m.CrosstabsPage }))
);
const DashboardsPage = React.lazy(() =>
  import("./components/analytics/dashboards/DashboardsPage").then((m) => ({ default: m.DashboardsPage }))
);

const MainContentFallback = () => <LoadingState label="Loading page…" layout="page" />;

const App = () => {
  useGlobalDialogEscape();
  const [path, setPath] = useState(window.location.pathname);
  const [search, setSearch] = useState(window.location.search);
  const [dashboardDesignPanelOpen, setDashboardDesignPanelOpen] = useState(true);
  const [dashboardDesignPanelTarget, setDashboardDesignPanelTarget] = useState<HTMLDivElement | null>(null);
  const dashboardDesignPanelActive =
    /^\/dashboards\/[^/]+\/edit$/.test(path) && new URLSearchParams(search).get("shared") !== "true";
  const auth = useAuth();
  const authBootstrapRoute = path === "/auth/callback" || path === "/auth/sso";
  const bellomyUser = isBellomyUser(auth.user);
  let audience = "";
  let audienceError: string | null = null;
  if (!authBootstrapRoute) {
    try {
      audience = getAudience();
    } catch (error) {
      if (!(error instanceof AudienceResolutionError)) throw error;
      audienceError = error.message;
    }
  }
  const availability = useFeatureAvailability(audience, path, auth.isAuthenticated && !authBootstrapRoute);
  const panelistFinderEnabled = bellomyUser || availability.data?.features["panelist-finder"] === true;
  const shellItems = useMemo(() => getShellNavigation(bellomyUser), [bellomyUser]);
  const shellAccount: AccountState = {
    status: "known",
    name: auth.user?.userDisplayName || auth.user?.userName || auth.user?.userID || "Signed-in user",
    ...(auth.user?.userName?.includes("@") ? { email: auth.user.userName } : {}),
  };
  const navigate = (nextPath: string) => {
    const [nextPathname, nextQuery = ""] = nextPath.split("?");
    const nextSearch = nextQuery ? `?${nextQuery}` : "";
    if (window.location.pathname === nextPathname && window.location.search === nextSearch) {
      return;
    }
    const proceed = () => {
      window.history.pushState({}, "", nextPath);
      setPath(nextPathname);
      setSearch(nextSearch);
    };
    if (!canNavigateTo(nextPath, proceed)) return;
    proceed();
  };
  const shellUserMenuItems: readonly UserMenuItem[] = auth.user?.groups?.includes("PG_BELLOMY_USERS")
    ? [{ id: "administration", label: "Administration", icon: shellSettingsIcon, onSelect: () => navigate("/admin") }]
    : [];

  const beforeShellNavigate = (target: NavigationTarget): boolean | void => {
    if (target.kind === "app" && target.id === "cocreation") {
      window.location.assign(COCREATION_TOOLS_HANDOFF_PATH);
      return false;
    }
  };

  React.useEffect(() => {
    const onPopState = () => {
      const previousPath = `${path}${search}`;
      const nextPath = `${window.location.pathname}${window.location.search}`;
      const nextPathname = window.location.pathname;
      const nextSearch = window.location.search;
      const proceed = () => {
        window.history.pushState({}, "", nextPath);
        setPath(nextPathname);
        setSearch(nextSearch);
      };
      if (!canNavigateTo(nextPath, proceed)) {
        window.history.pushState({}, "", previousPath);
        return;
      }
      setPath(window.location.pathname);
      setSearch(window.location.search);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [path, search]);

  React.useEffect(() => {
    if (dashboardDesignPanelActive) {
      setDashboardDesignPanelOpen(true);
    }
  }, [dashboardDesignPanelActive, path]);

  React.useEffect(() => {
    if (path === "/") {
      navigate(`/studies${search}`);
    } else if (path.startsWith("/insights") && !path.startsWith("/insights/cross-study-analysis")) {
      navigate("/studies");
    }
  }, [path, search]);

  React.useEffect(() => {
    if (auth.isAuthenticated) {
      const returnPath = localStorage.getItem("bri_return_path");
      if (returnPath) {
        localStorage.removeItem("bri_return_path");
        navigate(returnPath);
      }
    }
  }, [auth.isAuthenticated]);

  React.useEffect(() => {
    if (!auth.isAuthenticated || !panelistFinderEnabled || window.location.pathname.startsWith("/scheduling")) {
      return;
    }

    const preload = () => {
      void loadSchedulingPage();
    };
    const idleWindow = window as unknown as {
      requestIdleCallback?: Window["requestIdleCallback"];
      cancelIdleCallback?: Window["cancelIdleCallback"];
    };
    if (idleWindow.requestIdleCallback && idleWindow.cancelIdleCallback) {
      const idleId = idleWindow.requestIdleCallback(preload, { timeout: 1500 });
      return () => idleWindow.cancelIdleCallback?.(idleId);
    }
    const timerId = window.setTimeout(preload, 250);
    return () => window.clearTimeout(timerId);
  }, [auth.isAuthenticated, panelistFinderEnabled]);

  if (path === "/auth/sso") {
    return <SsoCallback />;
  }

  if (path === "/auth/callback") {
    return <AuthCallback />;
  }

  if (audienceError) {
    return (
      <main className="app-shell unauth">
        <div className="card auth-card" role="alert">
          <h1>Audience required</h1>
          <p>{audienceError}</p>
        </div>
      </main>
    );
  }

  if (auth.loading) {
    return (
      <main className="app-shell unauth">
        <LoadingState label="Checking authentication..." layout="page" />
      </main>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <main className="app-shell unauth">
        <div className="card auth-card">
          <h1>Panel Insights - Studies</h1>
          <p>Sign in to browse and manage studies.</p>
          <button className="primary-btn" type="button" onClick={auth.login}>
            Sign In
          </button>
        </div>
      </main>
    );
  }

  if (path === COCREATION_TOOLS_HANDOFF_PATH) {
    return <CocreationLaunchPage token={auth.token} />;
  }

  const match = path.match(/^\/studies\/([^/]+)$/);
  const detailStudyId = match ? decodeURIComponent(match[1]) : null;
  const dataExplorerMatch = path.match(/^\/data-explorer\/([^/]+)$/);
  const dataExplorerStudyId = dataExplorerMatch ? decodeURIComponent(dataExplorerMatch[1]) : undefined;
  const crosstabMatch = path.match(/^\/crosstabs\/([^/]+)$/);
  const crosstabItemId = crosstabMatch ? decodeURIComponent(crosstabMatch[1]) : undefined;
  const dashboardEditMatch = path.match(/^\/dashboards\/([^/]+)\/edit$/);
  const dashboardMatch = path.match(/^\/dashboards\/([^/]+)$/);
  const dashboardItemId = dashboardEditMatch ? decodeURIComponent(dashboardEditMatch[1]) : dashboardMatch ? decodeURIComponent(dashboardMatch[1]) : undefined;
  const dashboardEdit = Boolean(dashboardEditMatch) || path === "/dashboards/edit";
  const homeEditActive = path === "/home/edit";
  const homeActive = path === "/home" || homeEditActive;
  const studiesActive = path === "/studies" || path.startsWith("/studies/");
  const panelistsActive = path === "/panelists" || path.startsWith("/panelists/");
  const panelistDetailMatch = path.match(/^\/panelists\/([^/]+)$/);
  const initialPanelistId = panelistDetailMatch ? decodeURIComponent(panelistDetailMatch[1]) : undefined;
  const dataExplorerActive = path === "/data-explorer" || path.startsWith("/data-explorer/");
  const crosstabsActive = path === "/crosstabs" || path.startsWith("/crosstabs/");
  const dashboardsActive = path === "/dashboards" || path.startsWith("/dashboards/");
  const schedulingActive = path === "/scheduling" || path.startsWith("/scheduling/");
  const communicationActive = path === "/communication" || path.startsWith("/communication/");
  const panelChatActive = path === "/chat-with-panel" || path.startsWith("/chat-with-panel/");
  const automationActive = path === "/automation" || path.startsWith("/automation/");
  const toolsActive = path === "/tools" || path.startsWith("/tools/");
  const panelistDialogDesignerActive = path === "/tools/panelist-dialog-designer";
  const panelFieldManagerActive = path === "/tools/panel-field-manager";
  const studyFieldManagerActive = path === "/tools/study-field-manager";
  const studyDialogDesignerActive = path === "/tools/study-dialog-designer";
  const designSystemActive = path === "/tools/design-system";
  const panelistCategoriesActive = path === "/tools/panelist-categories";
  const panelFieldGroupsActive = path === "/tools/panel-field-groups";
  const dataBrowserActive = path === "/tools/data-browser" && isBellomyUser(auth.user);
  const dataBrowserInitialKey = dataBrowserActive ? new URLSearchParams(search).get("key") : null;
  const dataSearchActive = path === "/tools/data-search" && isBellomyUser(auth.user);
  const audienceCopyActive = path === "/tools/audience-copy" && isBellomyUser(auth.user);
  const featureAvailabilityActive = path === "/tools/feature-availability";
  const crossStudyReportActive = path === "/insights/cross-study-analysis/report";

  return (
    <>
      <SessionManager />
      <ExportProgressOverlay />
      <NotificationBanner />
      <div className="panel-tools-authenticated-app">
        <AppShell
          appId="panel-insights"
          productName="Panel Insights"
          appSwitcher={{ environment: appConfig.stage, apps: getShellApps() }}
          items={shellItems}
          currentId={getCurrentShellItemId(path)}
          homeHref="/home"
          onNavigate={(id) => {
            const destination = shellItems.find((item) => item.id === id)?.href;
            if (destination) navigate(destination);
          }}
          onNavigateHome={() => navigate("/home")}
          onBeforeNavigate={beforeShellNavigate}
          assistant={{ status: "unavailable", reason: "AskBell is not available in Panel Insights yet." }}
          account={shellAccount}
          onSignOut={auth.logout}
          userMenuItems={shellUserMenuItems}
          fit="parent"
          rightPanel={
            dashboardDesignPanelActive
              ? {
                  open: dashboardDesignPanelOpen,
                  onOpenChange: setDashboardDesignPanelOpen,
                  label: "Dashboard editor",
                  title: "Design",
                  content: <div ref={setDashboardDesignPanelTarget} />,
                  closeLabel: "Close dashboard design panel",
                  handleLabel: "Dashboard design panel",
                  showLabel: "Show dashboard design panel",
                  hideLabel: "Hide dashboard design panel"
                }
              : undefined
          }
        >
          <div className="main-content panel-tools-content">
          <FeatureGate path={path} data={availability.data} error={availability.error} onRetry={availability.reload} isBellomyUser={bellomyUser}>
          <Suspense fallback={<MainContentFallback />}>
            {featureAvailabilityActive ? (
              isBellomyUser(auth.user) ? (
                <FeatureAvailabilityPage key={audience} audience={audience} data={availability.data}
                  error={availability.error} onRetry={availability.reload} onSave={availability.save}
                  onBack={() => navigate("/tools")} />
              ) : <section className="page"><div className="card" role="alert">Only Bellomy users can manage feature availability.</div></section>
            ) : crossStudyReportActive ? (
              <CrossStudyReportPage />
            ) : homeActive ? (
              <HomePage
                onOpenPanelists={() => navigate("/panelists")}
                onOpenStudies={() => navigate("/studies")}
                onOpenStudy={(studyId) => navigate(`/studies/${encodeURIComponent(studyId)}`)}
                onOpenInsights={() => navigate("/insights")}
                onOpenCommunications={() => navigate("/communication")}
                user={auth.user}
                edit={homeEditActive}
                onEditLayout={!homeEditActive && bellomyUser ? () => navigate("/home/edit") : undefined}
                onExitLayoutEditor={() => navigate("/home")}
              />
            ) : panelistsActive ? (
              <PanelistsPage user={auth.user} initialPanelistId={initialPanelistId} />
            ) : dataExplorerActive ? (
              <DataExplorerPage studyId={dataExplorerStudyId} onNavigate={navigate} />
            ) : crosstabsActive ? (
              <CrosstabsPage itemId={crosstabItemId} onNavigate={navigate} />
            ) : dashboardsActive ? (
              <DashboardsPage
                itemId={dashboardItemId}
                edit={dashboardEdit}
                designPanelTarget={dashboardDesignPanelTarget}
                onNavigate={navigate}
              />
            ) : schedulingActive ? (
              <SchedulingPage />
            ) : communicationActive ? (
              <CommunicationsPage />
            ) : panelChatActive ? (
              <PanelChatPage onNavigate={navigate} />
            ) : automationActive ? (
              <AutomationPage />
            ) : panelistDialogDesignerActive ? (
              <LazyPanelistDialogDesignerPage onBack={() => navigate("/tools")} />
            ) : path === "/tools/panelist-screener-design" ? (
              isBellomyUser(auth.user) ? <LazyPanelistScreenerDesignPage onBack={() => navigate("/tools")} /> : <p role="alert">Bellomy access is required to edit screener designs.</p>
            ) : panelFieldManagerActive ? (
              <LazyPanelFieldManagerPage onBack={() => navigate("/tools")} />
            ) : studyFieldManagerActive ? (
              <LazyStudyFieldManagerPage onBack={() => navigate("/tools")} />
            ) : studyDialogDesignerActive ? (
              <LazyStudyDialogDesignerPage onBack={() => navigate("/tools")} />
            ) : designSystemActive ? (
              <LazyDesignSystemPage onBack={() => navigate("/tools")} user={auth.user} />
            ) : panelistCategoriesActive ? (
              <LazyPanelistCategoriesPage onBack={() => navigate("/tools")} />
            ) : panelFieldGroupsActive ? (
              <LazyPanelFieldGroupsPage onBack={() => navigate("/tools")} />
            ) : dataBrowserActive ? (
              <LazyDataBrowserPage onBack={() => navigate("/tools")} initialKey={dataBrowserInitialKey} />
            ) : dataSearchActive ? (
              <LazyDataSearchPage
                onBack={() => navigate("/tools")}
                onOpenInDataBrowser={(key) => navigate(`/tools/data-browser?key=${encodeURIComponent(key)}`)}
              />
            ) : audienceCopyActive ? (
              <LazyAudienceCopyToolPage onBack={() => navigate("/tools")} />
            ) : toolsActive ? (
              <ToolsPage onNavigate={navigate} showAdminTools={isBellomyUser(auth.user)} />
            ) : detailStudyId ? (
              <StudyDetailPage
                studyId={detailStudyId}
                onBack={() => navigate("/studies")}
                onNavigate={navigate}
              />
            ) : studiesActive ? (
              <StudiesInsightsPage user={auth.user} onOpenStudy={(studyId) => navigate(`/studies/${encodeURIComponent(studyId)}`)} />
            ) : (
              <HomePage
                onOpenPanelists={() => navigate("/panelists")}
                onOpenStudies={() => navigate("/studies")}
                onOpenStudy={(studyId) => navigate(`/studies/${encodeURIComponent(studyId)}`)}
                onOpenInsights={() => navigate("/insights")}
                onOpenCommunications={() => navigate("/communication")}
                user={auth.user}
              />
            )}
          </Suspense>
          </FeatureGate>
          </div>
        </AppShell>
      </div>
    </>
  );
};

export default App;
